import type { Schemas } from "@/shared/api/client";

export type ProjectMember = Schemas["ProjectMemberOut"];
export type ProjectRole = Schemas["ProjectRole"];
/** An active team member as pickers offer them (GET /team/people). */
export type StaffMember = Schemas["ColleagueOut"];

export const PROJECT_ROLES = [
  "lead",
  "contributor",
  "viewer",
] as const satisfies readonly ProjectRole[];

/** The lead first, then everyone else by name. */
export function sortMembers(members: readonly ProjectMember[], locale: string): ProjectMember[] {
  return [...members].sort((a, b) => {
    if (a.role === "lead" || b.role === "lead") return a.role === "lead" ? -1 : 1;
    return a.user.name.localeCompare(b.user.name, locale);
  });
}

export interface MemberRules {
  canChangeRole: boolean;
  canRemove: boolean;
}

/**
 * What a manager may do to a member. The lead's role cannot be changed or removed directly
 * (the server answers LAST_LEAD): making someone else lead hands the role over instead.
 */
export function memberRules(member: ProjectMember, canManage: boolean): MemberRules {
  const editable = canManage && member.role !== "lead";
  return { canChangeRole: editable, canRemove: editable };
}

/** Active staff who are not on the project yet: the people "Add member" offers. */
export function addableStaff(
  staff: readonly StaffMember[],
  members: readonly ProjectMember[],
): StaffMember[] {
  const onProject = new Set(members.map((member) => member.user.id));
  return staff.filter((person) => !onProject.has(person.id));
}

/** A staff member as tasks name people (assignees, mentions) outside any project. */
export function personOfStaff(member: StaffMember): Schemas["PersonOut"] {
  return {
    id: member.id,
    name: member.name,
    avatar_url: member.avatar_url,
    role: member.role,
    is_active: true, // the picker lists active people only
  };
}
